// src/features/document/chronicleLogic.ts
// Pure helpers for the document chronicle: turning documents, activity-service
// events and workflow pending decisions into one day-grouped event feed.

import type { Activity } from '@core/types/activity';
import type { PendingDecision } from '@core/types/workflow';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChronicleEventKind =
  | 'published'
  | 'approval'
  | 'signature'
  | 'generated'
  | 'dispute'
  | 'reminder'
  | 'created'
  | 'updated'
  | 'expiring';

export type ChronicleFilterId = 'all' | 'approvals' | 'signatures' | 'templates' | 'disputes' | 'deadlines';

export interface ChronicleEvent {
  id: string;
  at: Date;
  kind: ChronicleEventKind;
  /** One-sentence fact, already localized. */
  title: string;
  /** Second line of context (document number, pipeline stage, remaining acks). */
  context?: string;
  documentId?: string;
  /** Display name of the human behind the event, when the source carries one. */
  actorName?: string;
  actorId?: string;
  actorAvatar?: string;
  filter: ChronicleFilterId;
}

export interface ChronicleDayGroup {
  key: string;
  label: string;
  events: ChronicleEvent[];
}

// ---------------------------------------------------------------------------
// Timestamps — API sends ISO strings or proto Timestamp-like objects
// ---------------------------------------------------------------------------

export function toMs(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'string' || typeof value === 'number') {
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof value === 'object') {
    const obj = value as { seconds?: number | string; nanos?: number };
    const seconds = Number(obj.seconds ?? 0);
    if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000 + (obj.nanos ?? 0) / 1e6;
  }
  return 0;
}

function isZeroDate(ms: number): boolean {
  // Go zero time (0001-01-01) and pre-1971 stamps mean "never set".
  return ms === 0 || new Date(ms).getFullYear() < 1971;
}

// ---------------------------------------------------------------------------
// Documents → events. The list API carries status and timestamps — enough to
// derive the operational facts even when the activity stream is quiet.
// ---------------------------------------------------------------------------

interface DocLike {
  entity?: { id?: string };
  id?: string;
  title?: string;
  document_number?: string;
  status?: number | string;
  updated_at?: unknown;
  created_at?: unknown;
  next_review_date?: unknown;
  version?: number | string;
}

export function documentIdOf(doc: DocLike): string | undefined {
  return doc?.entity?.id || doc?.id || undefined;
}

const STATUS_EVENTS: Record<string, { kind: ChronicleEventKind; filter: ChronicleFilterId; titleKey: string }> = {
  published: { kind: 'published', filter: 'all', titleKey: 'published' },
  in_review: { kind: 'approval', filter: 'approvals', titleKey: 'inReview' },
  pending_approval: { kind: 'approval', filter: 'approvals', titleKey: 'pendingApproval' },
  approved: { kind: 'approval', filter: 'approvals', titleKey: 'approved' },
  rejected: { kind: 'approval', filter: 'approvals', titleKey: 'rejected' },
  revision: { kind: 'updated', filter: 'all', titleKey: 'revision' },
  draft: { kind: 'updated', filter: 'all', titleKey: 'draft' },
};

export function deriveDocumentEvents(
  documents: DocLike[],
  t: (key: string) => string,
  limit = 60,
): ChronicleEvent[] {
  const events: ChronicleEvent[] = [];
  for (const doc of documents) {
    const id = documentIdOf(doc);
    if (!id) continue;
    // Status arrives as the full enum name (DOCUMENT_STATUS_PUBLISHED).
    const statusKey = String(doc.status ?? '').toLowerCase().replace('document_status_', '');
    const spec = STATUS_EVENTS[statusKey];
    const updatedMs = toMs(doc.updated_at);
    const createdMs = toMs(doc.created_at);
    const base = isZeroDate(updatedMs) ? createdMs : updatedMs;
    if (!isZeroDate(createdMs)) {
      events.push({
        id: `${id}:created`,
        at: new Date(createdMs),
        kind: 'created',
        title: `${t('document.chronicle.event.created')} «${doc.title || t('common.untitled')}»`,
        context: doc.document_number || undefined,
        documentId: id,
        filter: 'all',
      });
    }
    if (spec && !isZeroDate(base)) {
      events.push({
        id: `${id}:status`,
        at: new Date(base),
        kind: spec.kind,
        title: `${t(`document.chronicle.event.${spec.titleKey}`)} «${doc.title || t('common.untitled')}»`,
        context: [doc.document_number, doc.version ? `v${doc.version}` : ''].filter(Boolean).join(' · ') || undefined,
        documentId: id,
        filter: spec.filter,
      });
    }
  }
  return events.sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Activity service → events. Tenant-wide stream filtered client-side to
// document objects (the REST filter has no object_type parameter yet).
// ---------------------------------------------------------------------------

/** Fallback copy when an activity arrives without a human-readable summary. */
const KIND_TITLE_KEY: Record<ChronicleEventKind, string> = {
  published: 'document.chronicle.event.published',
  approval: 'document.chronicle.event.approvalEvent',
  signature: 'document.chronicle.event.signatureEvent',
  generated: 'document.chronicle.event.generatedEvent',
  dispute: 'document.chronicle.event.disputeEvent',
  reminder: 'document.chronicle.event.reminderEvent',
  expiring: 'document.chronicle.event.expiringEvent',
  created: 'document.chronicle.event.created',
  updated: 'document.chronicle.event.updatedEvent',
};

const VERB_KINDS: Array<[RegExp, ChronicleEventKind, ChronicleFilterId]> = [
  [/publish/, 'published', 'all'],
  [/approv|review|reject|submit/, 'approval', 'approvals'],
  [/acknowledge|sign/, 'signature', 'signatures'],
  [/template|instantiat|generat/, 'generated', 'templates'],
  [/dispute|escalat/, 'dispute', 'disputes'],
  [/remind/, 'reminder', 'all'],
  [/expir|due/, 'expiring', 'deadlines'],
];

export function activityToEvents(
  activities: Activity[] | null | undefined,
  t: (key: string) => string,
  limit = 60,
): ChronicleEvent[] {
  if (!activities) return [];
  const events: ChronicleEvent[] = [];
  for (const activity of activities) {
    const objectTypeName = activity?.object?.type_name ?? '';
    if (objectTypeName && objectTypeName !== 'document') continue;
    const ms = toMs(activity.created_at);
    if (!ms) continue;
    const verb = activity.verb || '';
    let kind: ChronicleEventKind = 'updated';
    let filter: ChronicleFilterId = 'all';
    for (const [pattern, mappedKind, mappedFilter] of VERB_KINDS) {
      if (pattern.test(verb)) {
        kind = mappedKind;
        filter = mappedFilter;
        break;
      }
    }
    events.push({
      id: activity?.entity?.id ?? `${verb}:${ms}:${objectTypeName}`,
      at: new Date(ms),
      kind,
      // A summary is written for humans; the verb never was. Degrade to the
      // localized kind label instead of leaking an internal identifier.
      title: activity.summary || t(KIND_TITLE_KEY[kind]),
      documentId: activity?.object?.id ?? undefined,
      actorName: activity?.actor?.display_name || undefined,
      actorId: activity?.actor?.entity?.id,
      actorAvatar: activity?.actor?.avatar,
      filter,
    });
  }
  return events.sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Workflow pending decisions → "awaiting me" approval events
// ---------------------------------------------------------------------------

export function decisionsToEvents(
  decisions: PendingDecision[] | null | undefined,
  t: (key: string) => string,
): ChronicleEvent[] {
  if (!decisions) return [];
  const events: ChronicleEvent[] = [];
  for (const decision of decisions) {
    const subject = decision?.subject;
    if (subject?.type_name && subject.type_name !== 'document') continue;
    const ms = toMs(decision.created_at);
    events.push({
      id: decision?.entity?.id ?? `${decision.node_id}:${decision.slot_index}`,
      at: new Date(ms || Date.now()),
      kind: 'approval',
      title: t('document.chronicle.event.awaitsDecision'),
      context: decision.instructions || undefined,
      documentId: subject?.id ?? undefined,
      filter: 'approvals',
    });
  }
  return events;
}

// ---------------------------------------------------------------------------
// Merge + day grouping
// ---------------------------------------------------------------------------

export function mergeEvents(...sources: ChronicleEvent[][]): ChronicleEvent[] {
  const byId = new Map<string, ChronicleEvent>();
  for (const source of sources) {
    for (const event of source) {
      if (!byId.has(event.id)) byId.set(event.id, event);
    }
  }
  return Array.from(byId.values()).sort((a, b) => b.at.getTime() - a.at.getTime());
}

export function groupEventsByDay(
  events: ChronicleEvent[],
  locale = 'en',
  labels?: { today: string; yesterday: string },
): ChronicleDayGroup[] {
  const groups = new Map<string, ChronicleEvent[]>();
  const todayKey = new Date().toDateString();
  const yesterdayKey = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();
  const dayLabel = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
  const weekdayLabel = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' });

  for (const event of events) {
    const key = event.at.toDateString();
    const bucket = groups.get(key);
    if (bucket) bucket.push(event);
    else groups.set(key, [event]);
  }

  return Array.from(groups.entries()).map(([key, dayEvents]) => {
    const date = new Date(key);
    if (key === todayKey && labels) return { key, label: `${labels.today}, ${dayLabel.format(date)}`, events: dayEvents };
    if (key === yesterdayKey && labels) return { key, label: `${labels.yesterday}, ${dayLabel.format(date)}`, events: dayEvents };
    return { key, label: weekdayLabel.format(date), events: dayEvents };
  });
}

export function filterEvents(events: ChronicleEvent[], filter: ChronicleFilterId): ChronicleEvent[] {
  if (filter === 'all') return events;
  return events.filter((event) => event.filter === filter);
}

// ---------------------------------------------------------------------------
// Deadline chips (expiring rail)
// ---------------------------------------------------------------------------

export type DeadlineTone = 'red' | 'amber' | 'ok';

export function deadlineTone(ms: number): DeadlineTone {
  const days = Math.ceil((ms - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return 'red';
  if (days <= 14) return 'amber';
  return 'ok';
}

export function deadlineDaysLabel(ms: number, locale = 'en'): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
  const days = Math.round((ms - Date.now()) / (24 * 60 * 60 * 1000));
  return rtf.format(days, 'day');
}

/** Documents with a real next_review_date, soonest first. */
export function documentsExpiring(documents: DocLike[], withinDays = 45): Array<{ id: string; title: string; reviewMs: number; number?: string }> {
  const cutoff = Date.now() + withinDays * 24 * 60 * 60 * 1000;
  const result: Array<{ id: string; title: string; reviewMs: number; number?: string }> = [];
  for (const doc of documents) {
    const id = documentIdOf(doc);
    const reviewMs = toMs(doc.next_review_date);
    if (!id || isZeroDate(reviewMs) || reviewMs > cutoff) continue;
    result.push({ id, title: doc.title || '', reviewMs, number: doc.document_number || undefined });
  }
  return result.sort((a, b) => a.reviewMs - b.reviewMs);
}
